import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import axiosInstance, {
  getUserDetails,
  isFisherOwnerUser,
  isCompanyManagerUser,
  isSuperUserSession,
  mergeUserDetails,
  handleLegacySessionExpired,
} from "../../components/content/axiosConfig";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import { getTeamAccessRole } from "../../utils/teamRoles";
import TeamRolePicker from "../../components/staff/TeamRolePicker.jsx";
import { resolveScopedFisherID } from "../../utils/pondListForUser";
import { getScopedAccount } from "../../utils/accountContext";

const cardRadius = { borderRadius: "14px" };

const CreateStaff = () => {
  const history = useHistory();
  const userDetails = getUserDetails();
  const scopedAccount = getScopedAccount();
  const isCompanyManager = isCompanyManagerUser(userDetails);
  const isSuperScoped = isSuperUserSession(userDetails);
  // Company/Super must open a client account first — do not fall back to sandbox silently.
  const needsScopedAccount = isCompanyManager || isSuperScoped;
  const fisherID = needsScopedAccount
    ? scopedAccount?.id ?? null
    : resolveScopedFisherID(userDetails);
  const [isLoading, setIsLoading] = useState(false);
  const [ownerVerified, setOwnerVerified] = useState(null);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    accessRole: "pond_staff",
    status: true,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = getUserDetails();
      // Company/Super may add staff only to an opened/scoped account.
      if (isCompanyManagerUser(u) || isSuperUserSession(u)) {
        const scoped = getScopedAccount();
        if (!cancelled) setOwnerVerified(scoped?.id != null);
        return;
      }
      if (isFisherOwnerUser(u)) {
        if (!cancelled) setOwnerVerified(true);
        return;
      }
      const sid = u.id;
      if (!sid) {
        if (!cancelled) setOwnerVerified(false);
        return;
      }
      try {
        const { data } = await axiosInstance.get("api/User/GetStaff", { params: { id: sid } });
        const ok = !!(data && (data.IsFisherOwner === true || data.IsFisherOwner === 1));
        if (ok) mergeUserDetails({ IsFisherOwner: true });
        if (!cancelled) setOwnerVerified(ok);
      } catch {
        if (!cancelled) setOwnerVerified(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRoleChange = (accessRole) => {
    setFormData((prev) => ({ ...prev, accessRole }));
  };

  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (fisherID == null || fisherID === "") {
      showErrorToast(
        needsScopedAccount
          ? scopedAccount
            ? "Unable to resolve the active account. Leave and reopen the account, then try again."
            : isCompanyManager
              ? "Open a client account from Company Accounts first, then add staff."
              : "Open an account from SuperPanel first, then add staff."
          : "Unable to resolve your organization. Please sign in again."
      );
      setIsLoading(false);
      return;
    }

    if (!formData.fullName.trim()) {
      showErrorToast("Full name is required");
      setIsLoading(false);
      return;
    }
    if (!formData.email.trim()) {
      showErrorToast("Email is required");
      setIsLoading(false);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      showErrorToast("Please enter a valid email address");
      setIsLoading(false);
      return;
    }

    const roleDef = getTeamAccessRole(formData.accessRole);

    try {
      const { data } = await axiosInstance.post(
        "api/User/AddStaff",
        {
          FullName: formData.fullName,
          Email: formData.email.trim().toLowerCase(),
          IsAdmin: roleDef.isAdmin,
          IsFisherOwner: roleDef.isOwner,
          DefaultPondRole: roleDef.defaultPondRole,
          Status: formData.status,
          FisherID: fisherID,
        },
        { timeout: 15000 }
      );
      if (!roleDef.isOwner && data?.id != null && data.id !== "") {
        showSuccessToast("Member created — assign ponds next.");
        history.push(`/ScientistPond/${data.id}`);
      } else {
        showSuccessToast("Staff created successfully");
        history.push("/staff");
      }
    } catch (error) {
      setIsLoading(false);
      if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
        const rawMessage =
          error.code === "ECONNABORTED"
            ? "Request timed out. Staff may have been created; refresh the team list."
            : (error.response?.data?.message || "");
        const errorMessage = rawMessage.includes("another fisher account") || rawMessage.includes("another organization")
          ? "This email is already used in another organization. Use a different email to create an owner."
          : (rawMessage || "Unable to create staff member. Please try again.");
        showErrorToast(errorMessage);
      }
    }
  };

  if (ownerVerified === null) {
    return <Preloader />;
  }
  if (!ownerVerified) {
    return (
      <div className="donezo-page container-fluid px-3 px-md-4 py-5">
        <div
          className="mx-auto bg-white border-0 shadow-sm p-4 p-md-5 text-center"
          style={{ ...cardRadius, maxWidth: 440 }}
        >
          <div
            className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3 text-white"
            style={{ width: 56, height: 56, background: "#64748b" }}
          >
            <i className="fa fa-lock fs-4" />
          </div>
          <h2 className="h5 fw-semibold mb-2">
            {needsScopedAccount ? "Select an account first" : "Owner access required"}
          </h2>
          <p className="text-muted small mb-4">
            {isCompanyManager
              ? "Open a client account from Company Accounts, then add staff for that organization."
              : isSuperScoped
                ? "Open a pond-owner account from SuperPanel, then add staff for that organization."
                : "Only the pond owner can add team members. If you need access, ask your organization owner."}
          </p>
          <Link
            to={isCompanyManager ? "/company/accounts" : "/staff"}
            className="btn btn-primary rounded-pill px-4"
          >
            {isCompanyManager ? "Company Accounts" : "Back to team"}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="donezo-page">
      {isLoading ? (
        <Preloader />
      ) : (
        <form onSubmit={handleSubmit} className="container-fluid px-3 px-md-4 py-3 py-md-4">
          <nav aria-label="breadcrumb" className="mb-2">
            <ol className="breadcrumb mb-0 small">
              <li className="breadcrumb-item">
                <Link to="/" className="text-decoration-none text-muted">
                  Home
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link
                  to="/staff"
                  className="text-decoration-none text-muted"
                >
                  Team
                </Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Add member
              </li>
            </ol>
          </nav>

          <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-3 mb-4">
            <div>
              <h1 className="h3 fw-semibold text-dark mb-1">Add team member</h1>
              <p className="text-muted small mb-0">
                {scopedAccount?.name
                  ? `Adding staff for ${scopedAccount.name}. `
                  : null}
                Choose a role, then assign ponds on the next step. A temporary password will be emailed automatically.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2 ms-lg-auto">
              <Link
                to="/staff"
                className="btn btn-outline-secondary rounded-pill px-4 order-2 order-sm-1"
              >
                Cancel
              </Link>
              <button type="submit" className="btn btn-primary rounded-pill px-4 shadow-sm order-1 order-sm-2">
                <i className="fa fa-check me-2" />
                Save member
              </button>
            </div>
          </div>

          <div className="card border-0 shadow-sm" style={cardRadius}>
            <div className="card-body p-4 p-lg-5">
              <h2 className="h6 text-uppercase text-muted fw-semibold mb-3" style={{ letterSpacing: "0.04em" }}>
                Profile
              </h2>
              <div className="row g-3 mb-4">
                <div className="col-md-6">
                  <label htmlFor="fullName" className="form-label small fw-medium text-secondary">
                    Full name
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    className="form-control form-control-lg border-0 bg-light"
                    style={{ borderRadius: "10px" }}
                    placeholder="e.g. Jane Smith"
                    value={formData.fullName}
                    onChange={handleChange}
                    autoComplete="name"
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="email" className="form-label small fw-medium text-secondary">
                    Email (login)
                  </label>
                  <input
                    type="email"
                    id="email"
                    className="form-control form-control-lg border-0 bg-light"
                    style={{ borderRadius: "10px" }}
                    placeholder="name@organization.com"
                    value={formData.email}
                    onChange={handleChange}
                    autoComplete="email"
                  />
                </div>
              </div>

              <h2 className="h6 text-uppercase text-muted fw-semibold mb-3 mt-2" style={{ letterSpacing: "0.04em" }}>
                Role
              </h2>
              <p className="small text-muted mb-3">
                Select the member&apos;s primary role. Pond access is assigned separately after saving. To add a global
                scientist account, use <Link to="/inviteScientist">Invite independent scientist</Link>.
              </p>
              <TeamRolePicker
                value={formData.accessRole}
                onChange={handleRoleChange}
                idPrefix="create-staff-role"
              />

              <h2 className="h6 text-uppercase text-muted fw-semibold mb-3" style={{ letterSpacing: "0.04em" }}>
                Account status
              </h2>
              <div
                className="d-flex align-items-center justify-content-between flex-wrap gap-3 p-3 rounded-3 bg-light"
                style={{ borderRadius: "10px" }}
              >
                <div>
                  <div className="fw-medium text-dark">Active member</div>
                  <div className="small text-muted">Inactive accounts cannot sign in until enabled.</div>
                </div>
                <div className="form-check form-switch mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="status"
                    checked={formData.status}
                    onChange={handleChange}
                    style={{ width: "2.75rem", height: "1.35rem", cursor: "pointer" }}
                  />
                  <label className="form-check-label ms-2 small" htmlFor="status">
                    {formData.status ? "Active" : "Pending"}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default CreateStaff;
